import { Injectable, OnDestroy } from '@angular/core';
import {
  CompanyViewModel,
  CreateCompanyCommand,
  CreateFillSiteCommand,
  CreateQualifiedPersonCommand,
  CreateSourceSiteCommand,
  EditCompanyCommand,
  EditFillSiteCommand,
  EditQualifiedPersonCommand,
  EditSourceSiteCommand,
  FillSiteAttachmentViewModel,
  ICreateCompanyCommand,
  ICreateQualifiedPersonCommand,
  IEditCompanyCommand,
  QualifiedPersonViewModel,
  SourceSiteAttachmentViewModel
} from '@services/cleanfill-api/cleanfill-api.generated';
import { BehaviorSubject, Subscription } from 'rxjs';
import { HttpRegistrationService } from '../http-registration/http-registration.service';

@Injectable({
  providedIn: 'root'
})
export class StoreRegistrationService implements OnDestroy {
  subscriptions = new Subscription();
  private companySubject = new BehaviorSubject<CompanyViewModel>(null);
  company$ = this.companySubject.asObservable();
  private qpSubject = new BehaviorSubject<QualifiedPersonViewModel>(null);
  qp$ = this.qpSubject.asObservable();
  constructor(private httpRegistration: HttpRegistrationService) {}

  createCompany(createCompanyCommand: ICreateCompanyCommand, fn = (company: CompanyViewModel) => {}) {
    this.subscriptions.add(
      this.httpRegistration.createCompany(new CreateCompanyCommand(createCompanyCommand)).subscribe(company => {
        this.companySubject.next(company);
        fn(company);
      })
    );
  }
  userAcceptTerm(uniqueId: string) {
    return this.httpRegistration.userAcceptTerm(uniqueId).toPromise();
  }

  getTermsAndConditions() {
    return this.httpRegistration.getTermsAndConditions().toPromise();
  }

  getCompany(uniqueId: string) {
    this.subscriptions.add(
      this.httpRegistration.getCompany(uniqueId).subscribe(company => {
        this.companySubject.next(company);
        company.sourceSites.forEach(s => this.getSourceSite(s.uniqueId));
        company.fillSites.forEach(f => this.getFillSite(f.uniqueId));
      })
    );
  }

  editCompany(uniqueId: string, editCompany: IEditCompanyCommand, fn = () => {}) {
    this.subscriptions.add(this.httpRegistration.editCompany(uniqueId, new EditCompanyCommand(editCompany)).subscribe(fn));
  }

  deleteCompany(uniqueId: string, fn = () => {}) {
    this.subscriptions.add(
      this.httpRegistration.deleteCompany(uniqueId).subscribe(() => {
        this.companySubject.next(null);
        fn();
      })
    );
  }

  createSourceSite(command: CreateSourceSiteCommand) {
    this.subscriptions.add(
      this.httpRegistration.createSourceSite(command).subscribe(() => this.getCompany(this.companySubject.value.uniqueId))
    );
  }

  getSourceSite(uniqueId: string) {
    this.subscriptions.add(
      this.httpRegistration.getSourceSite(uniqueId).subscribe(s => {
        const company = this.companySubject.value;
        const sourceSiteIndex = this.companySubject.value.sourceSites.findIndex(source => source.uniqueId === s.uniqueId);
        if (sourceSiteIndex > -1) {
          company.sourceSites[sourceSiteIndex] = s;
        }
        this.companySubject.next(company);
      })
    );
  }

  editSourceSite(uniqueId: string, command: EditSourceSiteCommand, fn = () => {}) {
    this.subscriptions.add(this.httpRegistration.editSourceSite(uniqueId, command).subscribe(fn));
  }
  deleteSourceSite(uniqueId: string) {
    this.subscriptions.add(
      this.httpRegistration.deleteSourceSite(uniqueId).subscribe(() => this.getCompany(this.companySubject.value.uniqueId))
    );
  }
  getSourceSiteDocuments(uniqueId: string, fn: (docs: SourceSiteAttachmentViewModel[]) => void = () => {}) {
    this.subscriptions.add(this.httpRegistration.getSourceSiteAttachments(uniqueId).subscribe(fn));
  }
  createFillSite(command: CreateFillSiteCommand) {
    this.subscriptions.add(
      this.httpRegistration.createFillSite(command).subscribe(() => this.getCompany(this.companySubject.value.uniqueId))
    );
  }

  getFillSite(uniqueId: string) {
    this.subscriptions.add(
      this.httpRegistration.getFillSite(uniqueId).subscribe(f => {
        const company = this.companySubject.value;
        const fillSiteIndex = this.companySubject.value.fillSites.findIndex(fill => fill.uniqueId === f.uniqueId);
        if (fillSiteIndex > -1) {
          company.fillSites[fillSiteIndex] = f;
        }
        this.companySubject.next(company);
      })
    );
  }

  editFillSite(uniqueId: string, command: EditFillSiteCommand, fn = () => {}) {
    this.subscriptions.add(this.httpRegistration.editFillSite(uniqueId, command).subscribe(fn));
  }

  deleteFillSite(uniqueId: string) {
    this.subscriptions.add(
      this.httpRegistration.deleteFillSite(uniqueId).subscribe(() => this.getCompany(this.companySubject.value.uniqueId))
    );
  }
  getFillSiteDocuments(uniqueId: string, fn: (docs: FillSiteAttachmentViewModel[]) => void = () => {}) {
    this.subscriptions.add(this.httpRegistration.getFillSiteAttachments(uniqueId).subscribe(fn));
  }

  createQualifiedPerson(command: ICreateQualifiedPersonCommand, fn = (qp: QualifiedPersonViewModel) => {}) {
    this.subscriptions.add(
      this.httpRegistration.createQualifiedPerson(new CreateQualifiedPersonCommand(command)).subscribe(qp => {
        this.qpSubject.next(qp);
        fn(qp);
      })
    );
  }

  getQualifiedPerson(uniqueId: string) {
    this.subscriptions.add(this.httpRegistration.getQualifiedPerson(uniqueId).subscribe(qp => this.qpSubject.next(qp)));
  }

  editQualifiedPerson(uniqueId: string, command: EditQualifiedPersonCommand, fn = () => {}) {
    this.subscriptions.add(this.httpRegistration.editQualifiedPerson(uniqueId, command).subscribe(fn));
  }

  deleteQualifiedPerson(uniqueId: string, fn = () => {}) {
    this.subscriptions.add(
      this.httpRegistration.deleteQualifiedPerson(uniqueId).subscribe(() => {
        this.qpSubject.next(null);
        fn();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
